import { getAppUrl } from "@src/appV2/environment/getAppUrl";
import {
  confirmResetPassword,
  confirmSignIn,
  resetPassword,
  signIn,
  signOut,
} from "aws-amplify/auth";

import { type CognitoUserCredential } from "./cognitoAuth";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";

// Amplify's confirm step when Cognito requires the user to replace a temporary
// password (FORCE_CHANGE_PASSWORD status, e.g. an employee set a temporary
// password via AdminSetUserPassword with Permanent: false). It is completed
// inline with `confirmSignIn({ challengeResponse: newPassword })`.
const NEW_PASSWORD_REQUIRED_STEP = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

export async function signInWithPassword(
  email: string,
  password: string
): Promise<CognitoUserCredential> {
  let result;
  try {
    result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }

    await signOut();
    result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
  }

  if (result.nextStep.signInStep === "RESET_PASSWORD") {
    const error = new Error("Password reset required for the user");
    error.name = "PasswordResetRequiredException";
    throw error;
  }

  // The user was given a temporary password and must set their own before
  // sign-in can complete. Surface it as a distinct exception so the UI can show
  // the forced password-change screen and finish the flow via
  // `confirmSignInWithNewPassword` — instead of dead-ending on the missing token.
  if (result.nextStep.signInStep === NEW_PASSWORD_REQUIRED_STEP) {
    const error = new Error("A new password is required for the user");
    error.name = "NewPasswordRequiredException";
    throw error;
  }

  const session = await fetchAuthSessionWithLock();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error("Missing Cognito id token after password authentication");
  }

  return {
    idToken,
    accessToken: session.tokens?.accessToken?.toString(),
  };
}

/**
 * Completes a forced password change for a user whose `signInWithPassword`
 * surfaced `NewPasswordRequiredException`. Responds to the in-memory Amplify
 * challenge with the chosen password (`confirmSignIn`) and, once Cognito
 * reports the user as signed in, returns the resulting credential.
 *
 * Must run on the same browser that called `signInWithPassword`: the
 * NEW_PASSWORD_REQUIRED challenge lives in the in-memory Amplify session there.
 */
export async function confirmSignInWithNewPassword(
  newPassword: string
): Promise<CognitoUserCredential> {
  const result = await confirmSignIn({ challengeResponse: newPassword });

  if (!result.isSignedIn) {
    throw new Error(
      `Expected sign-in to complete after setting a new password but got ${result.nextStep.signInStep}`
    );
  }

  const session = await fetchAuthSessionWithLock();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error("Missing Cognito id token after setting a new password");
  }

  return {
    idToken,
    accessToken: session.tokens?.accessToken?.toString(),
  };
}

export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({
    username: email,
    options: {
      clientMetadata: {
        requester_url: `${getAppUrl()}/v2/reset-password`,
      },
    },
  });
}

export async function confirmPasswordReset(
  email: string,
  confirmationCode: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode,
    newPassword,
  });
}
